import { z } from "zod";
import {
  internalizePostalCode,
  unknownPostalCodeSchema,
  type ConventionalPostalCode,
} from "./postalCode";

const address = z.object({
  addressLine1: z.string(),
  addressLine2: z.string().nullish(),
  city: z.string(),
  postalCode: unknownPostalCodeSchema,
  additionalInformation: z.string().nullish(),
  keyToPatient: z.string().nullish(),
});
export type IAddress<PostalCodeType = string> = StrictOmit<
  z.infer<typeof address>,
  "postalCode"
> & {
  postalCode: PostalCodeType;
};

const coordinatesSchema = z.object({
  longitude: z.number().min(-180).max(180),
  latitude: z.number().min(-90).max(90),
});
export const addressWithCoordinates = address.extend({
  coordinates: coordinatesSchema,
});
export type IAddressWithCoordinates<PostalCodeType = string> = StrictOmit<
  z.infer<typeof addressWithCoordinates>,
  "postalCode"
> & {
  postalCode: PostalCodeType;
};

export const formatAddress = ({
  addressLine1,
  addressLine2,
  city,
  countryCode,
  postalCode,
  additionalInformation,
  keyToPatient,
  coordinates,
}: IAddressWithCoordinates<
  ConventionalPostalCode[keyof ConventionalPostalCode]
> & {
  countryCode: "SE" | "CH" | "LV";
}) => {
  return {
    addressLine1,
    addressLine2,
    city,
    postalCode: internalizePostalCode(postalCode, countryCode),
    additionalInformation,
    keyToPatient,
    coordinates: {
      // @ts-expect-error Longitude is usually a number, but can be a string if the input fields have been manually touched.
      longitude: parseFloat(coordinates.longitude),
      // @ts-expect-error Latitude is usually a number, but can be a string if the input fields have been manually touched.
      latitude: parseFloat(coordinates.latitude),
    },
    // The purpose of this type annotation is to ensure that when a new key is added to IAddressWithCoordinates, we get a compilation error if this return type does not include it.
  } satisfies Record<keyof IAddressWithCoordinates, unknown>;
};
